import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";

// Admin-only in-browser viewer for the investor pitch deck. Fetches the PDF
// as an authenticated blob, then renders via an object URL so admins can
// preview the deck even if PowerPoint/Adobe Reader chokes on their desktop.
export default function AdminDeck() {
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoke = "";
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/investor-deck`, {
          params: { fmt: "pdf" },
          responseType: "blob",
          withCredentials: true,
        });
        if (res.data?.type?.includes("application/json")) {
          const txt = await res.data.text();
          throw new Error(txt || "Failed to load deck");
        }
        const blob = new Blob([res.data], { type: "application/pdf" });
        const u = URL.createObjectURL(blob);
        revoke = u;
        setUrl(u);
      } catch (e) {
        const msg = e?.response?.status === 401 || e?.response?.status === 403
          ? "Admin sign-in required."
          : e?.response?.status === 404
          ? "Deck not generated yet on the server."
          : (e?.message || "Failed to load deck");
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, []);

  async function downloadAs(fmt) {
    const tid = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const res = await axios.get(`${API}/admin/investor-deck`, {
        params: { fmt }, responseType: "blob", withCredentials: true,
      });
      const blob = new Blob([res.data], {
        type: fmt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = u;
      a.download = `PetBond_Investor_Deck_${today}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(u), 2000);
      toast.success(`${fmt.toUpperCase()} downloaded`, { id: tid });
    } catch (e) {
      toast.error(e?.message || "Download failed", { id: tid });
    }
  }

  if (user && user.role !== "super_admin") {
    return (
      <div className="bg-surface border border-line rounded-3xl p-8 text-center" data-testid="deck-admin-only">
        <div className="font-heading text-xl font-bold">Admin only</div>
        <p className="text-ink-soft mt-1">This deck is restricted to PetBond team members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-deck-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/app/admin"
            data-testid="deck-back-link"
            className="rounded-full px-3 py-1.5 text-sm font-bold border border-line bg-surface text-ink-soft hover:border-brand hover:text-ink inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4"/>Back to Admin
          </Link>
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-70">PetBond Super Admin</div>
            <h1 className="font-heading text-xl md:text-2xl font-extrabold">Investor Pitch Deck</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="deck-download-pdf"
            onClick={() => downloadAs("pdf")}
            className="rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 bg-ink text-white border-ink hover:bg-brand hover:border-brand"
          >
            <Download className="w-4 h-4"/>PDF
          </button>
          <button
            type="button"
            data-testid="deck-download-pptx"
            onClick={() => downloadAs("pptx")}
            className="rounded-full px-4 py-2 text-sm font-bold border inline-flex items-center gap-2 bg-surface border-line text-ink-soft hover:border-brand hover:text-ink"
          >
            <FileText className="w-4 h-4"/>PPTX
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-line overflow-hidden bg-[#1a1a1a]" style={{ height: "calc(100vh - 220px)", minHeight: 540 }}>
        {loading && (
          <div className="h-full w-full flex items-center justify-center text-white/80" data-testid="deck-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2"/>Loading deck…
          </div>
        )}
        {!loading && error && (
          <div className="h-full w-full flex items-center justify-center text-white/80 p-6 text-center" data-testid="deck-error">
            <div>
              <div className="font-bold mb-1">Could not load deck</div>
              <div className="text-sm opacity-80">{error}</div>
            </div>
          </div>
        )}
        {!loading && !error && url && (
          <iframe
            data-testid="deck-iframe"
            src={url + "#view=FitH"}
            title="PetBond Investor Pitch Deck"
            className="w-full h-full bg-white"
          />
        )}
      </div>

      <div className="text-xs text-ink-soft">
        Tip: the PDF is rendered inline in your browser. Use <b>PDF</b> for sharing with VCs (consistent look), or <b>PPTX</b> if you need to edit a slide in PowerPoint/Keynote.
      </div>
    </div>
  );
}
